/**
 * Origin A on the 256 m plains (docs/design/C0_OPENING_PLAINS.md §5–§8): the opening cutscene (C1–C7), the wake-up
 * (W1–W6), the first hour's main chain (M1, M2, M2b, M3–M7) and the old quests re-homed as M8–M13, as data.
 *
 * In force only with the 256 m plains (`plains256()`, data/zones.ts): `QUESTS` (data/quests.ts) is this chain then,
 * and the scenes here (`bigPlains`) play only then. The 72 m flow keeps the elder's nine quests untouched. The rules
 * that run it (givers other than the elder, the new objectives, the spar, the masked men, the fair, the move
 * unlocks) are systems/plainsStory.ts. Every line is Thai, `{m:…|f:…}` picks a male or female hero's word (`bySex`),
 * and `/` in a bark splits it into bubbles shown in turn.
 */
import { OA, OA_QUESTS } from './story/originChains';
import type { QuestDef } from './quests';
import type { SceneDef } from './scenes';

type XY = readonly [x: number, y: number];

/** A line for a male or a female hero: `{m:…|f:…}` keeps the side of the hero's sex. */
export const bySex = (text: string, sex: 'm' | 'f'): string =>
  text.replace(/\{m:([^|}]*)\|f:([^}]*)\}/g, (_, m: string, f: string) => (sex === 'f' ? f : m));

/** The chain's quest ids, in order (`PLAINS_CHAIN`). */
export const PQ = {
  keeper: 'plains-keeper',
  forms: 'three-forms',
  stones: 'plains-stones',
  duel: 'plains-first-duel',
  wolves: 'wolves',
  bandits: 'bandits',
  horse: OA.horse, // H19
  bamboo: OA.log, // H19
  banners: 'plains-banners',
  masks: 'plains-masks',
  monks: 'monks',
  bears: 'bears',
  jiangshi: 'jiangshi',
  shadows: 'shadows',
  blackSword: 'black-sword',
  thousand: 'thousand-swords',
} as const;

/** The story's people on the plains who are not the World's own (the elder is). */
export type StoryRole = 'gao' | 'qiming' | 'leigang' | 'wang' | 'host' | 'tide' | 'lu';

export interface StoryNpc {
  readonly id: StoryRole;
  readonly name: string;
  readonly title: string;
  readonly sprite: string;
  /** Map metres on the 256 m plains (data/layouts/plains.ts); set on the nearest open floor. */
  readonly at: XY;
  readonly facing?: 1 | -1;
}

/** Where they stand: the cart by the south gate, the ring, the training hall, the fair at the hall of sect banners. */
export const STORY_CAST: readonly StoryNpc[] = [
  { id: 'gao', name: 'ลุงเกา', title: 'คนลากเกวียน · มีรอยสัญญาเลือด', sprite: 'stationMaster', at: [131.5, 148.5], facing: -1 },
  { id: 'qiming', name: 'ศิษย์พี่ฉีหมิง', title: 'ศิษย์สำนักคลื่นไผ่', sprite: 'watchman', at: [96.5, 100], facing: 1 },
  { id: 'leigang', name: 'เหล่ยกัง', title: 'ผู้คุมลาน', sprite: 'guardOfficer', at: [113.5, 102.5], facing: -1 },
  { id: 'wang', name: 'ครูฝึกเฒ่าหวัง', title: 'ครูฝึกแห่งโรงฝึกยุทธ์', sprite: 'watchman', at: [86.5, 114.5] },
  // The fair (ชุมนุมรับศิษย์) along the hall's banners (data/layouts/plains.ts puts six banners at y 86.5).
  { id: 'tide', name: 'ผู้อาวุโสเฉาหลิน', title: 'ผู้แทนสำนักคลื่นไผ่', sprite: 'fisherOld', at: [75.5, 84.5] },
  { id: 'host', name: 'ผู้ประกาศชุมนุม', title: 'ชุมนุมรับศิษย์', sprite: 'guide', at: [84.5, 84.5] },
  { id: 'lu', name: 'ผู้คุมทางหลู่เจิ้ง', title: 'ทางโคมเหล็ก', sprite: 'guardOfficer', at: [98.5, 84.5], facing: -1 },
];

/** The launch sects that exist as schools (data/schools.ts), each at its banner; คลื่นไผ่ is `tide` above. */
export const FAIR_RECRUITERS: readonly { readonly school: string; readonly at: XY }[] = [
  { school: 'cinnabar', at: [70.5, 84.5] },
  { school: 'mist-peak', at: [80.5, 84.5] },
  { school: 'pine-ink', at: [88.5, 84.5] },
];

/** The sect that has no school yet (C0_SECTS §1 `bamboo-tide`): a join is recorded for the sect system to come. */
export const TIDE_SECT = 'bamboo-tide';
export const WANDERER = 'wanderer';

/** Places the chain sends the player to (map metres), for the "go here" marker and the `visit` objective. */
export const STORY_PLACES: Readonly<Record<string, { readonly at: XY; readonly r: number }>> = {
  stele: { at: [128.5, 128.5], r: 14 },
  stones: { at: [152.5, 98.5], r: 10 },
  ring: { at: [104.5, 100.5], r: 10 },
  hall: { at: [84.5, 82.5], r: 9 },
  wood: { at: [200, 56], r: 12 },
  woodEdge: { at: [196, 66], r: 10 },
  hill: { at: [52, 200], r: 14 },
  bamboo: { at: [66, 50], r: 12 },
  temple: { at: [30, 116], r: 14 },
  ridge: { at: [230, 118], r: 14 },
  graves: { at: [190, 214], r: 14 },
  camp: { at: [34, 26], r: 12 },
  redcliff: { at: [230, 232], r: 10 },
  demonring: { at: [128, 22], r: 14 },
};

/** Where each quest's work is done (the marker while it is under way). */
export const QUEST_PLACE: Readonly<Record<string, string>> = {
  [PQ.forms]: 'ring',
  [PQ.duel]: 'ring',
  [PQ.stones]: 'stones',
  [PQ.wolves]: 'wood',
  [PQ.bandits]: 'hill',
  [PQ.banners]: 'hall',
  [PQ.masks]: 'bamboo',
  [PQ.monks]: 'temple',
  [PQ.bears]: 'ridge',
  [PQ.jiangshi]: 'graves',
  [PQ.shadows]: 'camp',
  [PQ.blackSword]: 'redcliff',
  [PQ.thousand]: 'demonring',
};

/** Who gives and takes back each quest when it is not the elder. */
export const QUEST_GIVER: Readonly<Record<string, StoryRole>> = {
  [PQ.forms]: 'qiming',
  [PQ.duel]: 'leigang',
  [PQ.stones]: 'wang',
  [PQ.bandits]: 'gao',
  [PQ.shadows]: 'lu',
};

/** Quests handed in by themselves the moment they are done (the giver is right there, or the work is far from him: the kill quests hand in on the spot). */
export const AUTO_TURN_IN: readonly string[] = [PQ.forms, PQ.duel, PQ.stones, PQ.banners, PQ.wolves, PQ.bandits];

/**
 * กระบี่ไผ่ห่มหมอก's moves in the order the chain teaches them (§9.1): 風 and 刺 from the start, then 掃 (M2), 蘆 (M3),
 * 竹 (the M4 offer), 霧 (ครูฝึกเฒ่าหวัง after M5), 颶 (M7), 颯 (M8, standing in for the คู่ฝึก ladder the plains lack).
 */
export const MOVES_ON_DONE: Readonly<Record<string, readonly string[]>> = {
  [PQ.forms]: ['bs-sweep'],
  [PQ.bandits]: ['bs-mist'],
  [PQ.masks]: ['bs-tempest'],
  [PQ.monks]: ['bs-glide'],
};
export const MOVES_ON_ACCEPT: Readonly<Record<string, readonly string[]>> = {
  [PQ.duel]: ['bs-reed'],
  [PQ.wolves]: ['bs-bamboo'],
};
/** 霧 is given when the bandits' escort is done (it was the teacher's, after a walk back). */
export const MIST_MOVE = 'bs-mist';

/** F2's route over the stream (docs/design/quests/2026-10-11-first-hour-f1-f4.md). Tiles are map metres, checked by test/plainsQuestsF.test.ts. */
export interface RouteDef {
  readonly id: string;
  /** Ordered checkpoints; reached when the hero stands within r. */
  readonly points: readonly { readonly at: XY; readonly r: number; readonly label: string }[];
  /** Falling into the stream sends the hero back here (no damage beyond the fall's own). */
  readonly resetTo: XY;
  /** The qinggong art that must be learned, else the objective shows `LINES.f2NeedArt`. */
  readonly needsArt: string;
}
/**
 * The stream at y 98 is tiles x 150-154 (the spec's y 98 was the tile edge, blocked; moved to 98.5, stones moved to
 * 150.5 / 152.5 / 154.5 so each lies in the water and the banks are the first free tiles: spec F2 note).
 */
export const ROUTES: Readonly<Record<string, RouteDef>> = {
  stones: {
    id: 'stones',
    points: [
      { at: [148.5, 98.5], r: 1.5, label: 'ฝั่งตะวันตก' },
      { at: [152.5, 98.5], r: 1, label: 'หินกลางน้ำ' },
      { at: [157.5, 98.5], r: 1.5, label: 'ฝั่งตะวันออก' },
    ],
    resetTo: [148.5, 98.5],
    needsArt: 'swallow-dash',
  },
};
/** The three stepping stones the scene lane builds (rise 0.3, 1.2 m wide): documented here for the check. */
export const STONES: readonly XY[] = [
  [150.5, 98.5],
  [152.5, 98.5],
  [154.5, 98.5],
];

/** F3's cart (spec F3). */
export interface EscortDef {
  readonly id: string;
  readonly npc: StoryRole;
  readonly from: XY;
  readonly to: XY;
  readonly path: readonly XY[];
  /** m/s, at most the hero's walk speed so the hero can lead. */
  readonly speed: number;
  readonly cartHp: number;
  readonly waves: readonly { readonly atPathIndex: number; readonly monsters: string; readonly n: number }[];
  /** On cartHp 0: the cart and the wave reset here; the quest stays active. */
  readonly failTo: XY;
}
export const ESCORTS: Readonly<Record<string, EscortDef>> = {
  cart: {
    id: 'cart',
    npc: 'gao',
    from: [79, 200],
    to: [68, 160],
    path: [
      [79, 200],
      [65, 169],
      [65, 163],
      [68, 160],
    ],
    speed: 1.4,
    cartHp: 120,
    waves: [
      { atPathIndex: 1, monsters: 'knifer', n: 2 },
      { atPathIndex: 2, monsters: 'bandit', n: 2 },
    ],
    failTo: [79, 200],
  },
};
/** The guards at the cart (kill objective of F3) and how hard a wave monster near the cart hits it, per second each. */
export const ESCORT_RULES = { guards: 3, guardMonster: 'bandit', hurtRange: 3.2, hurtPerSec: 3, holdRange: 7, leadRange: 9 } as const;
/** The first-hour title (on M7 done): id in data/titles.ts. */
export const FIRST_TITLE = 'first-step';

/** The masked men of M7 (data/monsters.ts) and where they gather at the bamboo edge (pocket K5). */
export const MASKED = 'masked';
export const MASKED_CHIEF = 'masked-chief';
export const MASK_SPOTS: readonly XY[] = [
  [62, 48],
  [70, 46],
  [66, 53],
  [74, 51],
];
export const CHIEF_SPOT: XY = [66, 45];
/** Where ฉีหมิง waits for the player in M7, at the edge of the bamboo. */
export const QIMING_M7: XY = [74, 57];

/** Lines (Thai). `/` splits a bark into bubbles. */
export const LINES = {
  // W6: the elder turns when the hero comes within 14 m.
  elderCall: 'อีกคนแล้วหรือ… เมื่อคืนแสงร่วงหลายสายนัก / มานี่เถิด',
  // M1 when cutscenes are off (a server, a test): the meeting as one dialog.
  keeperDialog:
    'ข้าหลิว เฝ้าศิลานี้มาทั้งชีวิต รอยที่มือเจ้าคือสัญญาเลือด ศิลาเลือกเจ้าแล้ว · เมื่อคืนมีชายหนุ่มวางมือที่ร่องที่เก้า ' +
    'ศิลาไม่รับเขา เขาลุกขึ้น แล้วเดินขึ้นเหนือไปในฝน · คนมีรอยมีคนล่าอยู่ เขาคงรู้ ยืนให้มั่นก่อน แล้วค่อยออกตามหา ไปหาฉีหมิงที่ลานประลอง',
  // M2 (ฉีหมิง)
  formsHello: 'คนมีรอยอีกคน ข้าฉีหมิง / ที่นี่มีกฎข้อเดียว ยืนให้อยู่ก่อน',
  forms1: 'ข้าจะฟันตรงๆ เห็นข้าง้าง ตั้งรับไว้',
  forms1Won: 'รับได้! ตั้งรับชนะท่าแท้',
  forms2: 'ทีนี้ข้าตั้งรับบ้าง / ใช้ท่า 刺 ทะลวงการ์ดข้าให้ได้',
  forms2Won: 'การ์ดข้าแตก… ท่าทำลายชนะตั้งรับ',
  forms3: 'ข้าจะชาร์จท่าทำลาย / ฟันขัดก่อนมันออก',
  forms3Won: 'สามท่าครบแล้ว! / นั่งสงบใจสักครู่ แล้วเปิดดูลมปราณ',
  formsOdd: 'แปลก เจ้าอ่านท่าข้าเหมือนใครบางคน',
  formsFail: 'ล้มเป็นก็ลุกเป็น อีกยก!',
  formsHints: ['รอให้เขาง้าง แล้วกดตั้งรับ', 'เขาตั้งรับอยู่ ใช้ท่าทำลาย', 'ตอนเขาชาร์จ ฟันขัดทันที'],
  qimingIdle: 'ฝึกให้มาก ล้มให้มาก แล้วจะยืนได้นาน',
  // M2b (the elder)
  sit1: 'รอยนั้นไม่ได้มาเปล่า มีกำลังภายในติดมา / นั่งลงข้างศิลา หายใจตามข้า',
  sitHint: 'นั่งสมาธิ (V) แล้วเปิดหน้าลมปราณ (N)',
  neigongOffer: 'ก่อนออกจากเขตปลอดภัย แวะใช้แต้มลมปราณก่อน จะได้ไม่เสียตัว',
  sitDone: 'ยามเจ้าไม่อยู่ ร่างเจ้ายังฝึกต่อ',
  // M3 (ผู้คุมลาน, the rival)
  duelRival: 'คนมีรอยหรือ ข้าอยากรู้ว่ามันเก่งจริงไหม',
  duelHint: 'ชนะสองยกก่อน ชนะการประลอง',
  duelLost: 'ยังอ่อนนัก กลับมาใหม่',
  duelWon: 'ยกหน้าข้าไม่ยอมแน่!',
  duelReed: 'ตรึงอยู่หมัด! นั่นแหละ 蘆 / ท่าล็อกเป้าใช้ได้ตอนมันเสียการ์ด',
  f1Offer: 'ชนะยกเดียวยังไม่พอ ข้าให้ท่าล็อกเป้า 蘆 ติดตัวไปแล้ว ทำลายการ์ดมัน แล้วตรึงมันไว้',
  f1Hint: 'กดเลือกเป้า ทำลายการ์ดให้แตก แล้วกดท่าล็อกเป้า',
  f1Done: 'นั่นแหละ 蘆 ตรึงคู่มือไว้ได้ ท่าล็อกเป้าใช้ได้ตอนมันเสียการ์ดเท่านั้น จำไว้',
  // F2 (ครูหวัง, the stones)
  f2Offer: 'ลำธารหน้าป่ามีหินสามก้อน ข้ามได้ถ้าเท้าเบาพอ เรียนวิชาตัวเบากับข้าก่อนไหม',
  f2Lesson: 'ย่อเข่า ปล่อยลมหายใจ / แล้วกระโดด มองหินก้อนถัดไป',
  f2Strip: 'ข้ามหินลอยน้ำ 3 ก้อน (กดกระโดด)',
  f2NeedArt: 'ต้องเรียนวิชาตัวเบา · คุยกับครูหวังที่โรงฝึกยุทธ์',
  f2Fall: '{m:โธ่|f:โอ๊ย} ตกน้ำเสียแล้ว กลับไปเริ่มใหม่',
  f2Done: 'ถึงฝั่งแล้ว! ถึงประตูป่าเร็วกว่าอ้อมสะพานตั้งครึ่งนาที',
  // F3 (ลุงเกา, the cart)
  f3Offer: 'เกวียนข้าถูกโจรยึดไว้ที่เนินโจร ช่วยปราบยาม แล้วพาเกวียนกลับทีเถอะ',
  f3Strip: 'ปราบยาม 3 คน ทำลายการ์ด 2 ครั้ง พาเกวียนถึงประตูเนินโจร',
  f3Guard: ['เกวียนนี้ของข้าแล้ว ถอยไป!', 'เด็กอย่างเจ้าจะกล้าสู้พวกข้าหรือ'],
  f3Wave: ['พวกเรามาแล้ว! ยึดเกวียนคืนมา!', 'ตามไป อย่าปล่อยให้ถึงประตู!'],
  f3CartHit: 'โอ๊ย! เกวียนข้า! ระวังหน่อย!',
  f3Reset: 'เกวียนพัง! ตั้งหลักใหม่ที่เดิม',
  f3Done: 'ครบทั้งคนทั้งเกวียน! ขอบใจ{m:เจ้าหนุ่ม|f:แม่นาง}มาก รับท่าหมอกไปเลย',
  // The first title (on M7 done)
  firstTitle: 'ชั่วโมงเดียวก็ได้ชื่อแล้ว ทุ่งราบนี้จำท่านได้',
  duelRematch: 'ลานนี้เปิดทุกวัน คู่ฝึกยืนรออยู่ทางตะวันออก',
  // M4
  woodEdge: 'พี่เดินขึ้นเหนือ… ทางนี้หรือเปล่า',
  // M5 → M6
  sectWhy: 'คนมีรอยที่ไม่มีสำนัก อยู่ได้ไม่นาน / พวกล่าคนมีรอย ชอบคนที่อยู่ลำพัง',
  mist: 'ตัวเบาแต่ใจหนัก ก็ล้มเหมือนกัน / ท่าหมอกลมคลุมกาย ข้าให้',
  wangIdle:
    'ตัวจะเบาได้ ใจต้องนิ่งก่อน ข้าสอนนกนางแอ่นพุ่งตัวกับวิ่งพันลี้ เปิดแท็บวิชาตัวเบาในหน้าต่างตัวละครแล้วเรียนได้เลย',
  // M6 (the fair)
  fairHost:
    'ชุมนุมรับศิษย์เปิดทุกวัน ผู้แทนแต่ละสำนักยืนอยู่ที่ธงของตน คุยกับเขาเพื่อฟังวิชาและเข้าสำนัก · ' +
    'หรือจะเดินทางลำพังก็เป็นทางหนึ่ง ข้าจดชื่อไว้ให้',
  fairWanderer: 'เดินทางลำพังหรือ ยุทธภพกว้าง ทางของเจ้าเจ้าเลือกเอง แต่อย่าลืมว่าพวกล่าคนมีรอยชอบคนที่อยู่ลำพัง',
  fairChosen: 'ดี มีคนยืนข้างเจ้าแล้ว',
  fairLater: 'เลือกสำนักได้ทุกวันที่หอธงสำนัก',
  tidePitch:
    'สำนักคลื่นไผ่ต้นทางของสามกระบวนท่า คลื่นซัดเข้า คลื่นถอยกลับ คลื่นแตกฝั่ง ฉีหมิงก็เป็นศิษย์ของเรา · ' +
    'วิชาประจำสำนักยังอยู่ระหว่างทาง ข้าจดชื่อเจ้าไว้ก่อน เมื่อสำนักเปิดรับเต็มตัว เจ้าจะเป็นศิษย์รุ่นแรก',
  tideMember: 'ศิษย์คลื่นไผ่ จำไว้ อ่านจังหวะก่อนฟัน',
  tideTaken: 'เจ้ามีทางของเจ้าแล้ว ข้ายินดีด้วย',
  recruiterDemo: 'ดูให้ดี วิชานี้ไม่ได้มีไว้โชว์',
  // M7
  gaoHook: 'ท่านผู้เฒ่า! ที่ป่าไผ่เหนือ / พวกใส่หน้ากากไม้จับคนตัดไม้ไป มันหาคนมีรอย!',
  gaoIdle: 'เจ้าก็โดนเหมือนข้า… มันเลือกคนอย่างเราทำไม',
  gaoAfter: 'เกวียนข้ายังซ่อมไม่เสร็จ แต่ข้าไม่ไปไหนหรอก',
  masksFirst: 'พวกมันง้างก่อนฟันเสมอ อ่านให้ออก',
  chiefSeen: 'อีกคนมีรอย… เอาตัวไป',
  chiefGuard: 'มันตั้งรับ! ท่าทำลาย!',
  chiefCharge: 'มันชาร์จ! ฟันขัด!',
  chiefFalls: 'ฆ่าข้าก็ไม่จบ / คนมีรอยไม่ได้มีแค่เจ้า',
  chiefCalls: 'ออกมา! จับมันไว้!',
  tassel: 'พู่กระบี่สีแดง… ของพี่',
  trialPitch:
    'อยากลองวิชาสำนักข้าก่อนไหม ข้ายืมวิชาให้ลองสู้กับศิษย์ซ้อมได้ ไม่เสียอะไร · ถ้าสนใจสมัครเป็นศิษย์ คุยกับข้าอีกครั้ง',
  trialGo: 'ลุยเลย! โจมตีเขาได้ ข้ายืมวิชาให้ชั่วครู่',
  trialWon: 'ดีมาก! เจ้ามีแวว / ถ้าถูกใจวิชานี้ คุยกับข้าอีกครั้งเพื่อสมัคร',
  trialEnd: 'พอแค่นี้ ข้าคืนวิชาเดิมให้แล้ว',
  chiefPhase2: 'มันชาร์จท่าทำลาย! ฟันขัดมัน!',
  chiefPhase1: 'มันตั้งรับแน่น! ใช้ท่าทำลายตัดการ์ด!',
  chiefPhase3: 'มันคลั่งแล้ว! ระวังให้ดี!',
  chiefGateOpen: 'มันอ่อนแรงลง ตามไปให้จบ!',
  gearDrop: 'ได้รับอาวุธจากหัวหน้าหน้ากากไม้ (มีค่าสถานะสุ่ม)',
  trialTask: 'ลองใช้ท่าที่ยืมมาให้ครบสามท่า',
  trialMore: 'ลองท่าอื่นอีก',
  wolvesNote: 'ส่งภารกิจที่ป่าแล้ว · ผู้เฒ่าหลิวรอเรื่องต่อไปที่ศิลา',
  autoOff: 'ภารกิจนี้ต้องลงมือเอง · ปิดต่อสู้อัตโนมัติให้แล้ว',
  qimingFights: 'ข้าสู้ข้างเจ้า! อย่าให้มันง้างใส่ข้า',
  gliderWon: 'ชนะนักพรตน้อยแล้วหรือ / ท่าลมย้อนผ่านพื้นหญ้า เป็นของเจ้า',
  masksFail: 'ถอยก่อน! ตั้งหลักแล้วกลับมาใหม่',
  sectReminder: 'ยังไม่ได้เลือกสำนัก · เลือกได้ทุกวันที่หอธงสำนัก',
  // M11
  luIdle: 'ทางโคมเหล็กเฝ้าทางเหนือ ใครมีเรื่องพวกหน้ากากไม้ มาบอกข้า',
} as const;

/** The 3-line card shown when the opening is skipped (§5: hold a key to skip). */
export const OPENING_RECAP: readonly string[] = [
  'พี่เขียนจดหมายบอกให้เจ้ามาที่ลานศิลา แล้วเขียนอีกฉบับว่า "อย่ามา"',
  'คืนฝนตก ศิลาเก้ากระบี่ลุกแดง แสงร่วงลงหาคนแปลกหน้า หนึ่งในนั้นคือเจ้า',
  'เจ้าตื่นที่ซุ้มประตูใต้ มีรอยสัญญาเลือดบนฝ่ามือ ไปหาผู้เฒ่าหลิวที่ศิลา',
];

/** The new quests' text; the re-homed ones take theirs from §7 (applied to the elder's quests by `plainsChain`). */
const NEW: Readonly<Record<string, QuestDef>> = {
  [OA.horse]: OA_QUESTS[0]!, // H19 (data/story/originChains.ts)
  [OA.log]: OA_QUESTS[1]!,
  [PQ.keeper]: {
    id: PQ.keeper,
    title: 'ผู้เฝ้าศิลา',
    offer: LINES.keeperDialog,
    remind: 'ผู้เฒ่าหลิวยืนอยู่ข้างศิลากลางลานศิลาเก้ากระบี่',
    done: 'ไปหาฉีหมิงที่ลานประลอง',
    objectives: [{ type: 'talk', target: 'elder', count: 1, label: 'คุยกับผู้เฒ่าหลิว' }],
    rewards: { cultivation: 0, gold: 0 },
  },
  [PQ.duel]: {
    id: PQ.duel,
    title: 'ท้าประลองครั้งแรก',
    offer:
      'ศิษย์ฉีหมิงว่าเจ้าพอไหว ลองของจริงไหม คู่ประลองยืนรออยู่ข้างลานนี่เอง คลิกคู่ประลอง แล้วคลิกขวาที่รูป เลือกท้าประลอง ' +
      'ชนะสองยกก่อน ชนะการประลอง แพ้ก็ไม่เสียอะไร ' + LINES.f1Offer,
    remind: 'คลิกคู่ประลองข้างลานประลอง แล้วคลิกขวาที่รูป เลือกท้าประลอง · ' + LINES.f1Hint,
    done: LINES.f1Done,
    objectives: [
      { type: 'duelWin', count: 1, label: 'ชนะการประลอง' },
      { type: 'lockForm', target: 'bs-reed', count: 1, label: 'ใช้ท่าล็อกเป้า 蘆 ใส่คู่ประลอง' },
    ],
    rewards: { cultivation: 100, gold: 60 },
    giver: { name: 'เหล่ยกัง ผู้คุมลาน', where: 'ที่ลานประลอง' },
  },
  [PQ.stones]: {
    id: PQ.stones,
    title: 'ข้ามหินลอยน้ำ',
    offer: LINES.f2Offer,
    remind: LINES.f2Strip,
    done: LINES.f2Done,
    objectives: [
      { type: 'talk', target: 'wang', count: 1, label: 'เรียนวิชาตัวเบาจากครูหวัง' },
      { type: 'route', target: 'stones', count: 3, label: LINES.f2Strip },
    ],
    rewards: { cultivation: 80, gold: 0 },
    giver: { name: 'ครูฝึกเฒ่าหวัง', where: 'ที่โรงฝึกยุทธ์' },
  },
  [PQ.banners]: {
    id: PQ.banners,
    title: 'ธงเหนือหอ',
    offer:
      'คนมีรอยที่ไม่มีสำนัก อยู่ได้ไม่นาน พวกล่าคนมีรอยชอบคนที่อยู่ลำพัง ไปหอธงสำนักเถิด ทางตะวันตกเฉียงเหนือ เลยลานประลองไป ' +
      'ผู้แทนแต่ละสำนักยืนรออยู่ที่ธง ดูให้ดีแล้วค่อยเลือก',
    remind: 'ไปหอธงสำนัก ลองวิชาแต่ละสำนัก แล้วเลือกทางของเจ้า · หอธงสำนักอยู่ตะวันตกเฉียงเหนือ เลยลานประลองไป',
    done: LINES.fairChosen,
    objectives: [
      { type: 'visit', target: 'hall', count: 1, label: 'ไปหอธงสำนัก' },
      { type: 'sect', count: 1, label: 'เลือกสำนัก หรือเดินทางลำพัง' },
    ],
    rewards: { cultivation: 150, gold: 50 },
  },
  [PQ.masks]: {
    id: PQ.masks,
    title: 'หน้ากากไม้',
    offer:
      'เป็นอย่างที่ข้ากลัว พวกใส่หน้ากากไม้จับคนตัดไม้ไปที่ป่าไผ่เหนือ มันหาคนมีรอย ฉีหมิงจะไปด้วยกัน ' +
      'ป่าไผ่อยู่ตะวันตกเฉียงเหนือ เลยหอธงสำนักไป พวกมันง้างก่อนฟันเสมอ อ่านให้ออก',
    remind: 'ป่าไผ่เหนืออยู่ตะวันตกเฉียงเหนือ เลยหอธงสำนักไป · หัวหน้าของมันตั้งรับเก่ง ใช้ท่าทำลาย และฟันขัดตอนมันชาร์จ',
    done: 'พู่นี้ผูกอยู่ที่ด้ามกระบี่เขาคืนนั้น',
    objectives: [
      { type: 'kill', target: MASKED, count: 4, label: 'คนหน้ากากไม้' },
      { type: 'kill', target: MASKED_CHIEF, count: 1, label: 'หัวหน้าหน้ากากไม้' },
    ],
    rewards: { cultivation: 600, gold: 100 },
  },
};

/** Text of the re-homed quests (§7 rewritten for the plains: no หยานหยู, no warps, places of this map). */
const REHOME: Readonly<Record<string, Partial<QuestDef>>> = {
  [PQ.forms]: {
    offer:
      'ผู้มาใหม่ ก่อนถือกระบี่ต้องเข้าใจสามกระบวนท่า โจมตีชนะท่าทำลาย ท่าทำลายชนะการตั้งรับ การตั้งรับชนะการโจมตี ' +
      'ข้าจะซ้อมกับเจ้าที่ลานประลองนี่ ตีข้าก่อนแล้วข้าจะสู้กลับ',
    remind: 'ลานประลองอยู่ตะวันตกเฉียงเหนือของลานศิลา · 攻 ให้ตั้งรับ · 守 ให้ใช้ท่าทำลาย 刺 · 破 ให้โจมตีขัดจังหวะ',
    done: 'สามท่าครบแล้ว นั่งสงบใจสักครู่ แล้วเปิดดูลมปราณ',
    giver: { name: 'ศิษย์พี่ฉีหมิง', where: 'ที่ลานประลอง' },
  },
  [PQ.wolves]: {
    offer:
      LINES.neigongOffer + ' · ' +
      'หมาป่าในป่าต้นน้ำดุผิดปกติ ราวมีบางสิ่งปลุกมัน ข้ามสะพานลานไปทางตะวันออกแล้วขึ้นเหนือ ก่อนไป เรียนลมหายใจไผ่เขียวไว้ ' +
      'ยามบาดเจ็บจะได้ฟื้นตัว กำจัดหกตัว',
    remind:
      'ป่าต้นน้ำอยู่เหนือสะพานลาน ตามลำธารขึ้นไป หมาป่าไม่ตั้งรับ โจมตีได้เต็มที่ · ตอนมันง้างกัด กดตั้งรับ (F) ให้ทันสองครั้ง แล้วส่งภารกิจเองที่ป่านั่นเลย',
    objectives: [
      { type: 'kill', target: 'wolf', count: 6, label: 'หมาป่าเงาจันทร์' },
      { type: 'block', count: 2, label: 'ตั้งรับการกัดของหมาป่า (F)' },
      { type: 'neigong', target: 'blood-oath', count: 1, label: 'ฝึกคัมภีร์สัญญาเลือด 1 ครั้ง' },
    ],
  },
  [PQ.bandits]: {
    offer: LINES.f3Offer,
    remind: LINES.f3Strip + ' · เนินโจรอยู่ทางตะวันตกเฉียงใต้ ออกซุ้มประตูใต้ แล้วไปทางตะวันตกตามทางเดินขึ้นเนิน',
    done: LINES.f3Done,
    giver: { name: 'ลุงเกา', where: 'ที่เกวียนประตูใต้' },
    objectives: [
      { type: 'kill', target: 'bandit', count: 3, label: 'โจรยามเกวียน' },
      { type: 'guardBreak', count: 2, label: 'ท่าทำลายตัดการ์ดโจร' },
      { type: 'escort', target: 'cart', count: 1, label: 'พาเกวียนถึงประตูเนินโจร' },
    ],
  },
  [PQ.monks]: {
    offer:
      'ทางตะวันตกมีวัดร้างระฆังร้าว เคยเป็นวัดสาขาของหินระฆัง บัดนี้พระนอกรีตยึดไว้ พวกมันตั้งรับเก่ง ใช้ท่าทำลายตัดการ์ดก่อน ' +
      'ปราบหกรูป แล้วข้าจะเล่าเรื่องกระบี่เล่มที่สิบให้ฟัง',
    remind: 'วัดร้างระฆังร้าวอยู่สุดถนนตะวันตก พระนอกรีตตั้งรับบ่อย ใช้ท่าทำลาย',
  },
  [PQ.bears]: {
    offer:
      'หมีดำในป่าสนผาหมีทางตะวันออกถูกปราณมารปลุกให้คลุ้มคลั่ง มันขย้ำทำลายการ์ด อย่ายืนรับ ให้หลบแล้วฟันสวน ปราบห้าตัว',
    remind: 'ป่าสนผาหมีอยู่ใต้ผาฝั่งตะวันออก เลยถนนตลาดไป ท่าขย้ำทำลายการตั้งรับ ให้หลบแทน',
  },
  [PQ.jiangshi]: {
    offer:
      'ที่สุสานเนินหมอกทางตะวันออกเฉียงใต้ ศพที่ถูกปราณมารปลุกลุกขึ้นกระโดดยามค่ำ เล็บของมันมีพิษ อย่าให้พิษทบซ้อน ปราบแปดตัว',
    remind: 'สุสานเนินหมอกอยู่ตะวันออกเฉียงใต้ ผีกระโดดออกมากยามค่ำ ระวังพิษสะสม',
  },
  [PQ.shadows]: {
    offer:
      'นักฆ่าเงาดำและโจรมีดบินซ่อนอยู่ในค่ายเงากลางป่าไผ่เหนือ พวกมันคือหูตาของจอมมาร ไปตัดหูตามันเสีย',
    remind: 'ค่ายเงาอยู่ในป่าไผ่ทางตะวันตกเฉียงเหนือ หลังหอธงสำนัก · โจรมีดบินมีบ้างที่ค่ายโจรบนเนินโจร',
    done: 'คำสั่งในค่ายเขียนว่า "ส่งขึ้นเหนือ" พวกหน้ากากส่งคนมีรอยขึ้นเหนือ จอมมารตาบอดไปข้างหนึ่งแล้ว',
    giver: { name: 'ผู้คุมทางหลู่เจิ้ง', where: 'ที่หอธงสำนัก' },
  },
  [PQ.blackSword]: {
    remind: 'ลานเลือดอยู่บนผาแดง มุมตะวันออกเฉียงใต้ เลยสุสานเนินหมอกไป ระวังวงปราณสีแดง ต้องหลบ ตั้งรับไม่ได้',
  },
};

/** The chain's order (§8.1): M1, M2, M2b, M3, M4–M7, then the old quests M8–M13. */
export const PLAINS_CHAIN: readonly string[] = [
  PQ.keeper,
  PQ.forms,
  PQ.duel,
  PQ.stones,
  PQ.wolves,
  PQ.bandits,
  PQ.horse,
  PQ.bamboo,
  PQ.banners,
  PQ.masks,
  PQ.monks,
  PQ.bears,
  PQ.jiangshi,
  PQ.shadows,
  PQ.blackSword,
  PQ.thousand,
];

/** The plains chain built from the elder's quests (`base`, data/quests.ts) and the new ones, in §8.1 order. */
export function plainsChain(base: readonly QuestDef[]): QuestDef[] {
  return PLAINS_CHAIN.map((id) => {
    const made = NEW[id];
    if (made) return made;
    const q = base.find((b) => b.id === id);
    if (!q) throw new Error(`plains chain: no quest ${id}`);
    return { ...q, ...REHOME[id] };
  });
}

// ───────────────────────── scenes ─────────────────────────

const ELDER = 'ผู้เฒ่าหลิว';
const GAO = 'ลุงเกา';
/** The hero's own name in a line (ui/cutscene.ts puts the player's name in). */
export const PLAYER_NAME = '@player';

/** C1–C7: rain, the brother at the ninth groove, his note, the title, the road, the lights, the raised hand, the voice. */
const opening: SceneDef = {
  id: 'plains-opening',
  kind: 'panels',
  zone: 'plains',
  bigPlains: true,
  // A new client0 character has the old ink opening counted as seen (apps/client0 newPlayer.ts): this follows it.
  trigger: { on: 'after', scene: 'opening-pact' },
  beforeQuest: 1,
  length: 34,
  actors: { me: { sprite: 'player', ink: true }, bro: { sprite: 'hero', ink: true } },
  cues: [
    { at: 0, do: 'fade', to: 'black', dur: 0 },
    { at: 0, do: 'panel', art: 'story-rain', dur: 4 },
    { at: 0.2, do: 'sfx', sfx: 'rain' },
    { at: 0.3, do: 'fade', to: 'clear', dur: 1.2 },
    { at: 0.6, do: 'caption', text: 'ร่องที่เก้า… ยังว่างอยู่', style: 'whisper', dur: 3 },
    { at: 0.6, do: 'sfx', sfx: 'whisper' },
    // C2
    { at: 4, do: 'panel', art: 'story-stele-night', dur: 5 },
    { at: 4.4, do: 'say', actor: 'bro', name: 'พี่ (กระซิบ)', text: 'ถ้าต้องมีคนหนึ่งลงนาม… ให้เป็นข้าคนเดียว', dur: 4 },
    { at: 5, do: 'sfx', sfx: 'heartbeat' },
    // C3
    { at: 9, do: 'panel', art: 'story-thrown', dur: 5 },
    { at: 9.1, do: 'sfx', sfx: 'boom' },
    { at: 9.8, do: 'say', actor: 'bro', name: 'พี่', text: 'อย่ามา… น้องข้า อย่ามาที่นี่', dur: 3.6 },
    // C4
    { at: 14, do: 'panel', art: 'pact-stone', dur: 4 },
    { at: 14.4, do: 'caption', text: 'เก้ากระบี่', style: 'title', dur: 3 },
    { at: 14.4, do: 'sfx', sfx: 'gong' },
    // C5, C5b
    { at: 18, do: 'panel', art: 'story-road', dur: 4.5 },
    { at: 18, do: 'sfx', sfx: 'rain' },
    { at: 18.2, do: 'sfx', sfx: 'letter' },
    { at: 18.3, do: 'caption', text: '…ถ้าเจ้าอ่านจดหมายนี้ จงมาหาพี่ที่ลานศิลา', style: 'ink', dur: 4 },
    { at: 22.5, do: 'panel', art: 'story-town-lights', dur: 3 },
    { at: 22.7, do: 'say', actor: 'me', name: PLAYER_NAME, text: 'อีกนิดเดียว… พี่ รอข้าก่อน', dur: 2.6 },
    // C6: the lights burst; the flash warning; one input that cannot fail.
    { at: 25.5, do: 'panel', art: 'story-lights', dur: 2.5 },
    { at: 25.5, do: 'sfx', sfx: 'lights' },
    { at: 25.6, do: 'caption', text: 'คำเตือน · ฉากต่อไปมีแสงวาบ', style: 'date', dur: 2.2 },
    { at: 27.9, do: 'wait', prompt: 'กด Space หรือคลิก เพื่อยกมือ' },
    // C7: palm meets light; four flashes of 0.6 s; white; the voice.
    { at: 28, do: 'panel', art: 'story-flash-hilt', dur: 0.6 },
    { at: 28, do: 'fade', to: 'white', dur: 0.08 },
    { at: 28, do: 'sfx', sfx: 'flash' },
    { at: 28.1, do: 'fade', to: 'clear', dur: 0.3 },
    { at: 28.6, do: 'panel', art: 'story-flash-grooves', dur: 0.6 },
    { at: 28.6, do: 'fade', to: 'white', dur: 0.08 },
    { at: 28.6, do: 'sfx', sfx: 'flash' },
    { at: 28.7, do: 'fade', to: 'clear', dur: 0.3 },
    { at: 29.2, do: 'panel', art: 'story-flash-marked', dur: 0.6 },
    { at: 29.2, do: 'fade', to: 'white', dur: 0.08 },
    { at: 29.2, do: 'sfx', sfx: 'flash' },
    { at: 29.3, do: 'fade', to: 'clear', dur: 0.3 },
    { at: 29.8, do: 'panel', art: 'story-flash-back', dur: 0.6 },
    { at: 29.8, do: 'fade', to: 'white', dur: 0.08 },
    { at: 29.8, do: 'sfx', sfx: 'flash' },
    { at: 29.9, do: 'fade', to: 'clear', dur: 0.3 },
    { at: 30.4, do: 'panel', art: 'pact-white', dur: 3.6 },
    { at: 30.4, do: 'fade', to: 'white', dur: 0.5 },
    { at: 30.6, do: 'fade', to: 'clear', dur: 0.6 },
    { at: 30.8, do: 'caption', text: 'ผู้ลงนามสัญญาเลือด… จงตื่น', style: 'whisper', dur: 3 },
    { at: 30.8, do: 'sfx', sfx: 'whisper' },
    { at: 33.2, do: 'fade', to: 'white', dur: 0.8 },
  ],
  end: {},
};

/** W1–W5: dawn at the south paifang; the note; the thought; ลุงเกา and his mark; the view NW to the stele. */
const wake: SceneDef = {
  id: 'plains-wake',
  kind: 'stage',
  zone: 'plains',
  bigPlains: true,
  trigger: { on: 'after', scene: 'plains-opening' },
  beforeQuest: 1,
  length: 12.5,
  actors: {
    me: { sprite: 'player' },
    gao: { sprite: 'stationMaster', standsIn: 'npc:ลุงเกา' },
  },
  cues: [
    { at: 0, do: 'fade', to: 'white', dur: 0 },
    { at: 0, do: 'place', actor: 'me', spot: { at: 'wake' }, face: [-1, -1] },
    { at: 0, do: 'pose', actor: 'me', pose: 'lie' },
    { at: 0, do: 'place', actor: 'gao', spot: { at: 'wake', dx: 3, dy: 3 }, face: [-1, -1] },
    { at: 0, do: 'pose', actor: 'gao', pose: 'sit' },
    // Far out (about half the usual view: the zoom's floor), easing in to the usual view by the hand-over.
    { at: 0, do: 'camera', focus: { actor: 'me' }, px: 1, dur: 0 },
    { at: 0, do: 'music', theme: 'none' },
    { at: 0.2, do: 'fade', to: 'clear', dur: 1.5 },
    { at: 0.4, do: 'sfx', sfx: 'bird' },
    { at: 1, do: 'fx', fx: 'palmGlow', actor: 'me', dur: 1.6 },
    { at: 1, do: 'sfx', sfx: 'palmGlow' },
    { at: 1.8, do: 'pose', actor: 'me', pose: 'sit' },
    { at: 2, do: 'caption', text: 'อย่ามา', style: 'ink', dur: 1.6 },
    { at: 2, do: 'sfx', sfx: 'letter' },
    { at: 3.4, do: 'pose', actor: 'me', pose: 'think' },
    { at: 3.4, do: 'say', actor: 'me', name: PLAYER_NAME, text: 'ลานศิลา… ข้ามาถึงแล้วหรือ', dur: 2.2 },
    { at: 5.8, do: 'camera', focus: { between: ['me', 'gao'] }, px: 2, dur: 1 },
    { at: 5.8, do: 'pose', actor: 'gao', pose: 'glance' },
    { at: 5.8, do: 'pose', actor: 'me', pose: 'idle' },
    { at: 5.9, do: 'say', actor: 'gao', name: GAO, text: '{m:พ่อหนุ่ม|f:แม่หนู} ฟื้นแล้วหรือ', dur: 2 },
    { at: 8, do: 'fx', fx: 'palmGlow', actor: 'gao', dur: 1.6 },
    { at: 8, do: 'say', actor: 'gao', name: GAO, text: 'เจ้าก็โดนเหมือนข้า… มันเลือกคนอย่างเราทำไม', dur: 3 },
    { at: 8, do: 'sfx', sfx: 'heartbeat' },
    { at: 8.6, do: 'music', theme: 'town' },
    { at: 11, do: 'camera', focus: { actor: 'me' }, px: 4, dur: 1.4 },
  ],
  end: { guideTo: 'elder', notice: 'คลิกที่พื้นหรือกด WASD เพื่อเดิน · ไปหาผู้เฒ่าหลิวที่ศิลา' },
};

/** M1 ผู้เฝ้าศิลา: the elder, the stone's choice, the brother who walked north. */
const keeper: SceneDef = {
  id: 'plains-keeper',
  kind: 'stage',
  zone: 'plains',
  bigPlains: true,
  trigger: { on: 'near', spot: { at: 'elder' }, r: 3 },
  beforeQuest: 1,
  length: 24,
  actors: { me: { sprite: 'player' }, elder: { sprite: 'elder', standsIn: 'elder' } },
  cues: [
    { at: 0, do: 'place', actor: 'elder', spot: { at: 'elder' }, face: [-1, 1] },
    { at: 0, do: 'pose', actor: 'elder', pose: 'fold' },
    { at: 0, do: 'camera', focus: { between: ['me', 'elder'] }, px: 4, dur: 1 },
    { at: 0, do: 'walk', actor: 'me', to: { at: 'elder', dx: -1.4, dy: 1 }, dur: 1.1 },
    { at: 1.2, do: 'pose', actor: 'me', pose: 'salute' },
    { at: 1.3, do: 'say', actor: 'elder', name: ELDER, text: 'ข้าหลิว เฝ้าศิลานี้มาทั้งชีวิต', dur: 2.4 },
    { at: 3.8, do: 'pose', actor: 'me', pose: 'idle' },
    { at: 3.8, do: 'fx', fx: 'palmGlow', actor: 'me', dur: 2.2 },
    { at: 3.8, do: 'sfx', sfx: 'palmGlow' },
    { at: 3.9, do: 'say', actor: 'elder', name: ELDER, text: 'รอยที่มือเจ้าคือสัญญาเลือด ศิลาเลือกเจ้าแล้ว', dur: 3.6 },
    { at: 7.6, do: 'say', actor: 'me', name: PLAYER_NAME, text: 'ข้ามาตามหาพี่ เขาให้ข้ามาที่นี่', dur: 2.4 },
    { at: 10.1, do: 'pose', actor: 'elder', pose: 'think' },
    { at: 10.1, do: 'camera', focus: { actor: 'elder' }, px: 5, dur: 1 },
    { at: 10.2, do: 'say', actor: 'elder', name: ELDER, text: 'เมื่อคืนมีชายหนุ่มวางมือที่ร่องที่เก้า ศิลาไม่รับเขา', dur: 3.8 },
    { at: 14.1, do: 'camera', focus: { between: ['me', 'elder'] }, px: 4, dur: 1 },
    { at: 14.1, do: 'say', actor: 'me', name: PLAYER_NAME, text: 'แล้วทำไมเขาเขียนว่า อย่ามา', dur: 2.4 },
    { at: 16.6, do: 'pose', actor: 'elder', pose: 'fold' },
    { at: 16.7, do: 'say', actor: 'elder', name: ELDER, text: 'คนมีรอยมีคนล่าอยู่ เขาคงรู้ · ยืนให้มั่นก่อน', dur: 3.4 },
    { at: 20.2, do: 'pose', actor: 'elder', pose: 'point' },
    { at: 20.3, do: 'say', actor: 'elder', name: ELDER, text: 'ไปหาฉีหมิงที่ลานประลอง', dur: 2.4 },
    { at: 22.8, do: 'pose', actor: 'me', pose: 'salute' },
  ],
  end: {},
};

/** M7's return: the tassel, the brother's sword, the rage kept, 颶 taught. */
const tassel: SceneDef = {
  id: 'plains-tassel',
  kind: 'stage',
  zone: 'plains',
  bigPlains: true,
  trigger: { on: 'questDone', quest: PQ.masks },
  length: 17,
  actors: { me: { sprite: 'player' }, elder: { sprite: 'elder', standsIn: 'elder' } },
  cues: [
    { at: 0, do: 'place', actor: 'elder', spot: { at: 'elder' }, face: [-1, 1] },
    { at: 0, do: 'pose', actor: 'elder', pose: 'think' },
    { at: 0, do: 'camera', focus: { between: ['me', 'elder'] }, px: 4, dur: 1 },
    { at: 0.6, do: 'say', actor: 'me', name: PLAYER_NAME, text: 'ทำไมพวกหน้ากากมีของพี่ข้า', dur: 2.8 },
    { at: 3.6, do: 'say', actor: 'elder', name: ELDER, text: 'ข้าไม่รู้', dur: 1.8 },
    { at: 5.5, do: 'pose', actor: 'elder', pose: 'fold' },
    { at: 5.5, do: 'say', actor: 'elder', name: ELDER, text: 'แต่ความโกรธที่เจ้าเก็บไว้ อย่าทิ้ง', dur: 3 },
    { at: 8.7, do: 'camera', focus: { actor: 'me' }, px: 5, dur: 0.8 },
    { at: 8.7, do: 'fx', fx: 'palmGlow', actor: 'me', dur: 2.6 },
    { at: 8.7, do: 'sfx', sfx: 'charge' },
    { at: 8.8, do: 'say', actor: 'elder', name: ELDER, text: 'ความโกรธเต็มเมื่อไร ปล่อยทีเดียวให้สุด', dur: 3.2 },
    { at: 12.2, do: 'caption', text: 'ได้รับกระบวนท่า 颶 พายุป่าไผ่', style: 'place', dur: 3 },
    { at: 12.2, do: 'sfx', sfx: 'gong' },
    { at: 15.4, do: 'pose', actor: 'me', pose: 'salute' },
  ],
  end: {},
};

/** The end card of the first hour (§6, M7). */
const endCard: SceneDef = {
  id: 'plains-end',
  kind: 'panels',
  zone: 'plains',
  bigPlains: true,
  trigger: { on: 'after', scene: 'plains-tassel' },
  length: 9,
  actors: {},
  cues: [
    { at: 0, do: 'fade', to: 'black', dur: 0 },
    { at: 0, do: 'panel', art: 'story-road', dur: 9 },
    { at: 0.2, do: 'fade', to: 'clear', dur: 1.2 },
    { at: 0.4, do: 'sfx', sfx: 'rain' },
    { at: 1, do: 'caption', text: 'พี่ยังอยู่ไหม… และใครเขียนให้ข้ามา', style: 'ink', dur: 4 },
    { at: 5.4, do: 'caption', text: 'จบบทแรก · จดหมายของพี่', style: 'title', dur: 3 },
    { at: 5.4, do: 'sfx', sfx: 'gong' },
    { at: 8.2, do: 'fade', to: 'black', dur: 0.8 },
  ],
  end: { notice: 'เป้าหมายต่อไป · ไปยังสถานีม้าปลายถนน' }, // TODO-polish: ch2 hook (data/story/ch2Hook.ts)
};

/** The plains scenes, in the order the runner looks for them (data/scenes.ts `SCENE_LIST`). */
export const PLAINS_SCENES: readonly SceneDef[] = [opening, wake, keeper, tassel, endCard];
